import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import AOS from "aos";
import "aos/dist/aos.css";
import { FiShield, FiCheckCircle, FiFileText, FiUsers, FiServer, FiCheck, FiArrowRight } from "react-icons/fi";

const ISMSCertificationPage = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [animationTriggered, setAnimationTriggered] = useState(false);

  useEffect(() => {
    // AOS 애니메이션 라이브러리 초기화
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
      easing: 'ease-out-cubic'
    });
    
    // 스크롤 이벤트 리스너
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);

      // 통계 섹션 확인
      if (statsRef.current) {
        const statsTop = statsRef.current.getBoundingClientRect().top;
        if (statsTop < window.innerHeight * 0.75 && !statsVisible) {
          setStatsVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [statsVisible]);

  // 스크롤을 기반으로 헤더 스타일 변경
  const headerOpacity = Math.max(0, Math.min(1, 1 - scrollPosition / 500));
  
  // 메인 섹션으로 스크롤
  const scrollToMain = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <PageContainer>
      {/* 역동적인 헤더 섹션 */}
      <HeroSection ref={headerRef} style={{ opacity: headerOpacity }}>
        <HeroOverlay>
          <HeroContent>
            <HeroSuptitle data-aos="fade-down">G-VISION TECHNOLOGY</HeroSuptitle>
            <HeroTitle data-aos="fade-up" data-aos-delay="200">
              ISMS <TitleHighlight>인증</TitleHighlight>
            </HeroTitle>
            <HeroDescription data-aos="fade-up" data-aos-delay="400">
              높은 수준의 정보보호 관리체계를 구축하고
              <br />안전한 비즈니스 환경을 제공합니다.
            </HeroDescription>
            <HeroButtonsContainer data-aos="fade-up" data-aos-delay="600">
              <PrimaryButton>인증 상담 신청</PrimaryButton>
              <SecondaryButton>자료 다운로드</SecondaryButton>
            </HeroButtonsContainer>
          </HeroContent>
          <ScrollIndicator onClick={scrollToMain}>
            <ScrollIcon />
            <ScrollText>자세히 알아보기</ScrollText>
          </ScrollIndicator>
        </HeroOverlay>
        <HeroBackgroundContainer>
          <HeroBackground />
          <HeroVideoOverlay />
        </HeroBackgroundContainer>
      </HeroSection>

      {/* 핵심 통계 섹션 */}
      <StatsSection ref={statsRef}>
        <StatsContainer>
          <StatCard data-aos="zoom-in" data-aos-delay="0">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "100%" : "0%"}
            </StatNumber>
            <StatName>인증 성공률</StatName>
            <StatDescription>그래비젼과 함께한 모든 고객사의 ISMS 인증 획득 성공</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="100">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "80%" : "0%"}
            </StatNumber>
            <StatName>보안 위험 감소</StatName>
            <StatDescription>ISMS 인증 획득 후 보안 관련 위험 및 사고 발생 감소율</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="200">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "104+" : "0"}
            </StatNumber>
            <StatName>통제 항목</StatName>
            <StatDescription>ISMS 인증을 위한 종합적인 보안 통제 항목 관리</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="300">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "50+" : "0"}
            </StatNumber>
            <StatName>인증 지원 기업</StatName>
            <StatDescription>국내외 다양한 기업의 ISMS 인증 획득을 지원한 경험</StatDescription>
          </StatCard>
        </StatsContainer>
      </StatsSection>

      {/* 소개 섹션 */}
      <IntroSection>
        <SectionContainer>
          <SectionHeading>
            <SectionTitleTag data-aos="fade-right">ABOUT</SectionTitleTag>
            <SectionTitle data-aos="fade-right" data-aos-delay="100">
              ISMS <ColoredText>인증이란?</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-right" data-aos-delay="200">
              정보보호 관리체계 인증으로 기업의 정보 자산을 안전하게 보호
            </SectionSubtitle>
          </SectionHeading>

          <IntroContainer>
            <IntroImageColumn data-aos="fade-right">
              <IntroImageWrapper>
                <IntroImage src={require("../../aimg/logo_isms.png")} alt="ISMS 인증" />
                <IntroImageDecoration />
              </IntroImageWrapper>
            </IntroImageColumn>
            
            <IntroContentColumn data-aos="fade-left" data-aos-delay="200">
              <IntroParagraph>
                ISMS(Information Security Management System) 인증은 기업이 정보 보호를 위해 수립, 관리, 운영하는 종합적인 체계가 국제 표준에 적합한지를 평가하는 제도입니다. 조직의 주요 정보자산을 보호하기 위한 관리적, 기술적, 물리적 보안 대책을 포함하며, 국내 정보통신망법에 따라 의무적으로 인증을 받아야 하는 기업들이 있습니다.
              </IntroParagraph>

              <IntroFeatureList>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiShield /></IntroFeatureIcon>
                  <IntroFeatureText>체계적인 보안 관리 시스템 구축</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiCheckCircle /></IntroFeatureIcon>
                  <IntroFeatureText>기업 신뢰도 및 대외 이미지 향상</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiFileText /></IntroFeatureIcon>
                  <IntroFeatureText>법적 요구사항 준수 및 규제 대응</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiUsers /></IntroFeatureIcon>
                  <IntroFeatureText>보안 사고 예방 및 대응 능력 강화</IntroFeatureText>
                </IntroFeatureItem>
              </IntroFeatureList>
              
              <LearnMoreButton>
                인증 절차 알아보기 <FiArrowRight />
              </LearnMoreButton>
            </IntroContentColumn>
          </IntroContainer>
        </SectionContainer>
      </IntroSection>

      {/* 핵심 기능 섹션 */}
      <FeaturesSection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">FEATURES</SectionTitleTag>
            <SectionTitle data-aos="fade-up">
              인증 <ColoredText>핵심 영역</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-up">
              ISMS 인증의 주요 관리 영역으로 체계적인 정보보호 체계 구축
            </SectionSubtitle>
          </SectionHeading>

          <FeatureCardsSection>
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(0)}>
              <FeatureCardInner active={activeFeature === 0}>
                <FeatureCardIcon active={activeFeature === 0}>
                  <FiFileText />
                </FeatureCardIcon>
                <FeatureCardTitle>관리체계 수립 및 운영</FeatureCardTitle>
                <FeatureCardDescription>
                  정보보호 정책 및 조직 구성
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(1)}>
              <FeatureCardInner active={activeFeature === 1}>
                <FeatureCardIcon active={activeFeature === 1}>
                  <FiShield />
                </FeatureCardIcon>
                <FeatureCardTitle>보호대책 구현</FeatureCardTitle>
                <FeatureCardDescription>
                  기술적/물리적 보안 통제 구현
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(2)}>
              <FeatureCardInner active={activeFeature === 2}>
                <FeatureCardIcon active={activeFeature === 2}>
                  <FiUsers />
                </FeatureCardIcon>
                <FeatureCardTitle>인적 보안</FeatureCardTitle>
                <FeatureCardDescription>
                  내부 직원 보안 교육 및 관리
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(3)}>
              <FeatureCardInner active={activeFeature === 3}>
                <FeatureCardIcon active={activeFeature === 3}>
                  <FiServer />
                </FeatureCardIcon>
                <FeatureCardTitle>위험 관리</FeatureCardTitle>
                <FeatureCardDescription>
                  보안 취약점 점검 및 위험 평가
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
          </FeatureCardsSection>
          
          <FeatureDetailSection>
            {activeFeature === 0 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_governance_framework.png")} alt="관리체계 수립 및 운영" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>관리체계 수립 및 운영</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      정보보호 관리체계의 기본 토대를 마련하는 단계로, 조직의 정보보호 정책 수립, 조직 구성, 경영진 책임 정의 등을 포함합니다. 
                      체계적인 문서화와 지속적인 검토 및 개선 프로세스를 구축합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 정보보호 정책 수립 및 문서화</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 정보보호 조직 구성 및 역할 정의</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 경영진 참여 및 책임 강화</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 지속적인 관리체계 검토 및 개선</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 1 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_security_controls.png")} alt="보호대책 구현" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>보호대책 구현</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      조직의 정보 자산을 보호하기 위한 기술적, 물리적 보안 통제를 구현하는 단계입니다.
                      네트워크 보안, 시스템 접근 통제, 암호화, 물리적 보안 등 다양한 보호 조치를 포함합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 네트워크 및 시스템 보안 구현</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 접근 통제 및 권한 관리</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 암호화 및 데이터 보호</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 물리적 보안 및 환경 통제</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 2 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_human_security.png")} alt="인적 보안" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>인적 보안</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      조직 내 모든 구성원들의 보안 인식을 높이고 안전한 행동을 유도하기 위한 교육 및 관리 체계입니다.
                      직원 채용부터 퇴사까지 전 과정에서의 보안 관리를 포함합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 정기적인 보안 교육 및 인식 프로그램</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 직무별 맞춤형 보안 훈련</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 내부자 위협 관리 및 모니터링</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 퇴직자 및 계약직 인력 보안 관리</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 3 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_risk_management.png")} alt="위험 관리" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>위험 관리</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      조직의 정보자산에 대한 위험을 식별, 평가하고 적절한 대응책을 마련하는 과정입니다.
                      주기적인 취약점 진단과 위험평가를 통해 선제적 대응 체계를 구축합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 정보자산 식별 및 중요도 평가</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 취약점 진단 및 모의해킹</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 위험 평가 및 분석</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 위험 대응 전략 수립 및 이행</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
          </FeatureDetailSection>
        </SectionContainer>
      </FeaturesSection>

      {/* 인증 절차 섹션 */}
      <TechSpecSection>
        <TechSpecBackground>
          <SectionContainer>
            <TechSpecContent data-aos="fade-up">
              <TechSpecLeft>
                <SectionHeading light>
                  <SectionTitleTag light>CERTIFICATION PROCESS</SectionTitleTag>
                  <SectionTitle light>
                    인증 <ColoredText light>절차</ColoredText>
                  </SectionTitle>
                  <SectionSubtitle light>
                    ISMS 인증 획득을 위한 단계별 절차
                  </SectionSubtitle>
                </SectionHeading>
                <TechSpecDescription>
                  ISMS 인증은 준비부터 심사, 인증 획득까지 체계적인 단계를 거쳐 진행됩니다.
                  그래비젼은 각 단계별로 최적화된 지원 서비스를 제공하여 고객사의 성공적인 인증 획득을 돕습니다.
                  인증 후에도 지속적인 유지관리를 위한 컨설팅을 제공합니다.
                </TechSpecDescription>
                <TechSpecDownloadButton>
                  인증 안내서 다운로드 <FiArrowRight />
                </TechSpecDownloadButton>
              </TechSpecLeft>
              
              <TechSpecRight>
                <TechSpecTable>
                  <TechSpecRow>
                    <TechSpecLabel>1단계</TechSpecLabel>
                    <TechSpecValue>사전 준비 및 현황 분석</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>2단계</TechSpecLabel>
                    <TechSpecValue>관리체계 수립 및 문서화</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>3단계</TechSpecLabel>
                    <TechSpecValue>보호대책 구현 및 운영</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>4단계</TechSpecLabel>
                    <TechSpecValue>내부 점검 및 취약점 개선</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>5단계</TechSpecLabel>
                    <TechSpecValue>인증 심사 준비 및 대응</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>6단계</TechSpecLabel>
                    <TechSpecValue>인증 심사 및 부적합 조치</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>7단계</TechSpecLabel>
                    <TechSpecValue>인증 취득 및 사후 관리</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>소요 기간</TechSpecLabel>
                    <TechSpecValue>평균 4-6개월 (기업 규모 및 준비 상태에 따라 상이)</TechSpecValue>
                  </TechSpecRow>
                </TechSpecTable>
              </TechSpecRight>
            </TechSpecContent>
          </SectionContainer>
        </TechSpecBackground>
      </TechSpecSection>

      {/* 구현 사례 섹션 */}
      <CaseStudySection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">SUCCESS STORIES</SectionTitleTag>
            <SectionTitle data-aos="fade-up" data-aos-delay="100">
              인증 <ColoredText>성공 사례</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-up" data-aos-delay="200">
              그래비젼과 함께 ISMS 인증을 성공적으로 획득한 고객 사례
            </SectionSubtitle>
          </SectionHeading>
          
          <CaseStudyGrid>
            <CaseStudyCard 
              data-aos="fade-up"
              data-aos-delay="0"
              onMouseEnter={() => {
                if (!animationTriggered) {
                  setAnimationTriggered(true);
                }
              }}
            >
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case1.png")} alt="핀테크 기업" />
                <CaseStudyCategory>핀테크</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>국내 선도 핀테크 기업</CaseStudyTitle>
                <CaseStudyDescription>
                  빠르게 성장하는 핀테크 기업의 ISMS 인증 획득을 지원하여 
                  금융 보안 신뢰도를 높이고 대외 경쟁력 강화
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>40%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>보안 위험 감소</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>3개월</CaseStudyResultNumber>
                    <CaseStudyResultLabel>인증 기간 단축</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
            
            <CaseStudyCard data-aos="fade-up" data-aos-delay="100">
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case2.png")} alt="의료 서비스" />
                <CaseStudyCategory>헬스케어</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>디지털 헬스케어 플랫폼</CaseStudyTitle>
                <CaseStudyDescription>
                  민감한 의료 정보를 다루는 헬스케어 기업의 ISMS 인증 획득으로
                  정보보호 체계 강화 및 사용자 신뢰 확보
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>100%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>규제 준수율</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>60%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>사용자 증가</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
            
            <CaseStudyCard data-aos="fade-up" data-aos-delay="200">
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case3.png")} alt="이커머스 기업" />
                <CaseStudyCategory>이커머스</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>대형 이커머스 플랫폼</CaseStudyTitle>
                <CaseStudyDescription>
                  대규모 고객 데이터를 처리하는 이커머스 기업의 ISMS 인증으로
                  개인정보 보호 강화 및 고객 신뢰도 향상
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>90%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>취약점 개선</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>ZERO</CaseStudyResultNumber>
                    <CaseStudyResultLabel>보안 사고</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
          </CaseStudyGrid>
          
          <ViewMoreButtonContainer data-aos="fade-up">
            <ViewMoreButton>
              모든 사례 보기 <FiArrowRight />
            </ViewMoreButton>
          </ViewMoreButtonContainer>
        </SectionContainer>
      </CaseStudySection>

      {/* CTAs 섹션 */}
      <CTASection>
        <SectionContainer>
          <CTAContainer data-aos="fade-up">
            <CTAContent>
              <CTATitle>
                기업 신뢰도 향상을 위한 <ColoredText>ISMS 인증</ColoredText>이 필요하신가요?
              </CTATitle>
              <CTADescription>
                그래비젼의 ISMS 인증 컨설팅으로 안전한 정보보호 체계를 구축하세요.
                <br />전문가와 상담을 통해 귀사에 맞는 인증 전략을 제안해 드립니다.
              </CTADescription>
              <CTAButtonsContainer>
                <CTAPrimaryButton>무료 상담 신청</CTAPrimaryButton>
                <CTASecondaryButton>자료 요청하기</CTASecondaryButton>
              </CTAButtonsContainer>
            </CTAContent>
            <CTAImageContainer>
              <CTAImage src={require("../../aimg/cta_image.png")} alt="ISMS 상담" />
            </CTAImageContainer>
          </CTAContainer>
        </SectionContainer>
      </CTASection>

      {/* 추가 제품 정보 섹션 */}
      <RelatedSection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">RELATED SOLUTIONS</SectionTitleTag>
            <SectionTitle data-aos="fade-up" data-aos-delay="100">
              함께 살펴보면 좋은 <ColoredText>솔루션</ColoredText>
            </SectionTitle>
          </SectionHeading>
          
          <RelatedProductGrid>
            <RelatedProductCard data-aos="fade-up" data-aos-delay="0">
              <RelatedProductImage src={require("../../aimg/logo_bitcoin.png")} alt="거래소 엔진" />
              <RelatedProductTitle>거래소 엔진</RelatedProductTitle>
              <RelatedProductDescription>
                안정성과 확장성을 갖춘 강력한 금융거래 엔진 솔루션을 제공합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
            
            <RelatedProductCard data-aos="fade-up" data-aos-delay="100">
              <RelatedProductImage src={require("../../aimg/logo_deepleaning1.png")} alt="딥러닝" />
              <RelatedProductTitle>딥러닝</RelatedProductTitle>
              <RelatedProductDescription>
                방대한 데이터를 분석하여 고도의 패턴 인식과 예측을 실현합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
            
            <RelatedProductCard data-aos="fade-up" data-aos-delay="200">
              <RelatedProductImage src={require("../../aimg/logo_ai_modeling1.png")} alt="AI 모델링" />
              <RelatedProductTitle>AI 모델링</RelatedProductTitle>
              <RelatedProductDescription>
                최신 AI 알고리즘으로 맞춤형 데이터 분석 모델을 개발합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
          </RelatedProductGrid>
        </SectionContainer>
      </RelatedSection>
    </PageContainer>
  );
};

// 애니메이션 정의
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// const slideInRight = keyframes`
//   from { transform: translateX(50px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

// const slideInLeft = keyframes`
//   from { transform: translateX(-50px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// const bounce = keyframes`
//   0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
//   40% { transform: translateY(-20px); }
//   60% { transform: translateY(-10px); }
// `;

const scrollDown = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateY(20px); opacity: 0; }
`;

// 스타일 컴포넌트
const PageContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  font-family: 'Noto Sans KR', sans-serif;
  padding-top: 80px; /* 네비게이션 바 높이 */
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

// 헤더 섹션
const HeroSection = styled.section`
  width: 100%;
  height: 100vh;
  position: relative;
  min-height: 700px;
  
  @media (max-width: 768px) {
    min-height: 600px;
  }
`;

const HeroBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${require("../../aimg/carousel4bg.png")}) no-repeat;
  background-size: cover;
  background-position: center;
  animation: ${pulse} 15s infinite ease-in-out;
`;

const HeroVideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(64, 72, 226, 0.8) 0%, rgba(0, 0, 0, 0.8) 100%);
`;

const HeroOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 0 20px;
  text-align: center;
  color: #ffffff;
`;

const HeroSuptitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 3px;
  margin-bottom: 20px;
  opacity: 0.9;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 30px;
  letter-spacing: -1px;
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const TitleHighlight = styled.span`
  color: #4048E2;
  display: inline-block;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: rgba(64, 72, 226, 0.3);
    z-index: -1;
    
    @media (max-width: 768px) {
      height: 6px;
      bottom: 3px;
    }
  }
`;

const HeroDescription = styled.p`
  font-size: 24px;
  font-weight: 300;
  margin-bottom: 40px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 30px;
  }
`;

const HeroButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PrimaryButton = styled.button`
  background-color: #4048E2;
  color: white;
  font-size: 18px;
  font-weight: 500;
  padding: 15px 35px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(64, 72, 226, 0.4);
  
  &:hover {
    background-color: #3038C2;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(64, 72, 226, 0.6);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
    width: 100%;
    max-width: 250px;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: white;
  font-size: 18px;
  font-weight: 500;
  padding: 15px 35px;
  border-radius: 30px;
  border: 2px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
    width: 100%;
    max-width: 250px;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ScrollIcon = styled.div`
  width: 30px;
  height: 50px;
  border: 2px solid white;
  border-radius: 25px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    width: 6px;
    height: 6px;
    background-color: white;
    border-radius: 50%;
    transform: translateX(-50%);
    animation: ${scrollDown} 2s infinite;
  }
`;

const ScrollText = styled.div`
  color: white;
  font-size: 14px;
  margin-top: 10px;
  font-weight: 300;
`;

// 통계 섹션
const StatsSection = styled.section`
  padding: 80px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  padding: 0 20px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #4048E2;
  margin-bottom: 15px;
  transition: opacity 0.5s ease-out;
  
  ${props => props.visible && css`
    animation: ${fadeIn} 1s forwards;
  `}
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const StatName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatDescription = styled.div`
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// 소개 섹션
const IntroSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.div`
  margin-bottom: 60px;
  text-align: ${props => props.centered ? 'center' : 'left'};
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionTitleTag = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#4048E2'};
  margin-bottom: 15px;
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.light ? 'white' : '#333'};
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ColoredText = styled.span`
  color: ${props => props.light ? 'white' : '#4048E2'};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: ${props => props.light ? 'rgba(255, 255, 255, 0.2)' : 'rgba(64, 72, 226, 0.2)'};
    z-index: -1;
    
    @media (max-width: 768px) {
      height: 6px;
      bottom: 3px;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 20px;
  font-weight: 300;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#666'};
  max-width: 600px;
  margin: ${props => props.centered ? '0 auto' : '0'};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const IntroContainer = styled.div`
  display: flex;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const IntroImageColumn = styled.div`
  flex: 1;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const IntroImageWrapper = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid #4048E2;
    border-radius: 10px;
    z-index: -1;
  }
`;

const IntroImage = styled.img`
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const IntroImageDecoration = styled.div`
  position: absolute;
  top: 20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: rgba(64, 72, 226, 0.1);
  border-radius: 50%;
  z-index: -1;
  animation: ${float} 6s infinite ease-in-out;
`;

const IntroContentColumn = styled.div`
  flex: 1;
`;

const IntroParagraph = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #444;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const IntroFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
`;

const IntroFeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IntroFeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(64, 72, 226, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: #4048E2;
  font-size: 20px;
`;

const IntroFeatureText = styled.div`
  font-size: 16px;
  color: #444;
`;

const LearnMoreButton = styled.button`
  background: none;
  border: none;
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

// 기능 섹션
const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

// 카드 섹션
const FeatureCardsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FeatureCard = styled.div`
  cursor: pointer;
  transform: translateY(0);
  transition: transform 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureCardInner = styled.div`
  background-color: ${props => props.active ? '#4048E2' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 15px;
  padding: 35px 25px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: ${props => props.active 
    ? '0 15px 35px rgba(64, 72, 226, 0.4)' 
    : '0 10px 25px rgba(0, 0, 0, 0.05)'};
  transition: all 0.4s ease;
  
  &:hover {
    box-shadow: ${props => props.active 
      ? '0 20px 40px rgba(64, 72, 226, 0.5)' 
      : '0 15px 35px rgba(0, 0, 0, 0.1)'};
  }
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const FeatureCardIcon = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
  color: ${props => props.active ? 'white' : '#4048E2'};
  transition: all 0.3s ease;
  
  ${FeatureCardInner}:hover & {
    animation: ${pulse} 1s infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 15px;
  }
`;

const FeatureCardTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const FeatureCardDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FeatureDetailSection = styled.div`
  position: relative;
  min-height: 500px;
  
  @media (max-width: 768px) {
    min-height: 400px;
  }
`;

const FeatureDetailPanel = styled.div`
  background-color: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.5s ease;
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const FeatureDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeatureDetailImage = styled.div`
  img {
    width: 100%;
    border-radius: 15px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureDetailContent = styled.div`
  
`;

const FeatureDetailTitle = styled.h3`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const FeatureDetailDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }
`;

const FeatureDetailList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureDetailItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 18px;
  color: #444;
  
  svg {
    color: #4048E2;
    margin-right: 15px;
    flex-shrink: 0;
    font-size: 20px;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
    
    svg {
      font-size: 18px;
      margin-right: 10px;
    }
  }
`;

// 기술 스펙 섹션
const TechSpecSection = styled.section`
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const TechSpecBackground = styled.div`
  background: linear-gradient(135deg, #373B98 0%, #4048E2 100%);
  border-radius: 30px;
  padding: 80px 40px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    border-radius: 20px;
  }
`;

const TechSpecContent = styled.div`
  display: flex;
  gap: 60px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const TechSpecLeft = styled.div`
  flex: 1;
`;

const TechSpecDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TechSpecDownloadButton = styled.button`
  background: white;
  color: #4048E2;
  border: none;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }
`;

const TechSpecRight = styled.div`
  flex: 1;
`;

const TechSpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
`;

const TechSpecRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const TechSpecLabel = styled.td`
  padding: 20px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  width: 40%;
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;
  }
`;

const TechSpecValue = styled.td`
  padding: 20px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;
  }
`;

// 구현 사례 섹션
const CaseStudySection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CaseStudyImageContainer = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const CaseStudyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${CaseStudyCard}:hover & {
    transform: scale(1.1);
  }
`;

const CaseStudyCategory = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #4048E2;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 15px;
  border-radius: 20px;
`;

const CaseStudyContent = styled.div`
  padding: 30px;
`;

const CaseStudyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CaseStudyDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CaseStudyResults = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const CaseStudyResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CaseStudyResultNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #4048E2;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CaseStudyResultLabel = styled.div`
  font-size: 13px;
  color: #666;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ViewMoreButton = styled.button`
  background: none;
  border: 2px solid #4048E2;
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 30px;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: #4048E2;
    color: white;
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 25px;
  }
`;

// CTA 섹션
const CTASection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 60px;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const CTAContent = styled.div`
  flex: 1;
  padding: 60px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.3;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #555;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const CTAPrimaryButton = styled.button`
  background: #4048E2;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(64, 72, 226, 0.2);
  
  &:hover {
    background: #3038C2;
    transform: translateY(-3px);
    box-shadow: 0 15px 25px rgba(64, 72, 226, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 25px;
  }
`;

const CTASecondaryButton = styled.button`
  background: none;
  border: 2px solid #4048E2;
  color: #4048E2;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(64, 72, 226, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 25px;
  }
`;

const CTAImageContainer = styled.div`
  flex: 1;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    min-height: 300px;
  }
`;

const CTAImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// 관련 제품 섹션
const RelatedSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const RelatedProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedProductCard = styled.div`
  background: #f9f9f9;
  border-radius: 15px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
  }
`;

const RelatedProductImage = styled.img`
  width: 160x;
  height: 160px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const RelatedProductTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const RelatedProductDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RelatedProductLink = styled.a`
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export default ISMSCertificationPage;